import { Toaster as Sonner } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import {
	QueryClient,
	QueryClientProvider,
	HydrationBoundary,
	DehydratedState,
} from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Index from './pages/Home';
import NotFound from './pages/NotFound';
import Services from './pages/Services';
import Contact from './pages/Contact';
import About from './pages/About';
import Blog from './pages/Blog';
import Support from './pages/Support';
import ServiceDetails from './pages/ServiceDetails';
import WorkshopDetailsPage from './pages/WorkshopDetails';
import ScrollToTop from './components/utils/ScrollToTop';
import SanityStudio from './pages/SanityStudio';
import BlogPost from './pages/BlogPost';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Loader from './components/ui/Loader';
import {
	useAboutPage,
	useBlogPosts,
	useContactPage,
	usePrivacyPage,
	useTermsPage,
	useWorkshops,
	useServicesPage,
	useFaq,
	useTestimonials,
	usePartners,
	useStats,
	useSiteSettings,
} from './hooks/useSanityData';
import { useState, useEffect } from 'react';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import { Header } from './components/layout/Header';

// Create a client
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
			staleTime: 1000 * 60 * 5, // 5 minutes
		},
	},
});

const AppContent = () => {
	const location = useLocation();
	const isStudio = location.pathname.startsWith('/studio');
	// Track loading states
	const { isLoading: isLoadingAbout, data: aboutData } = useAboutPage();
	const { isLoading: isLoadingBlog, data: blogData } = useBlogPosts();
	const { isLoading: isLoadingContact, data: contactData } = useContactPage();
	const { isLoading: isLoadingPrivacy, data: privacyData } = usePrivacyPage();
	const { isLoading: isLoadingTerms, data: termsData } = useTermsPage();
	const { isLoading: isLoadingWorkshops, data: workshopsData } = useWorkshops();
	const { isLoading: isLoadingServices, data: servicesData } =
		useServicesPage();
	const { isLoading: isLoadingFaq } = useFaq();
	const { isLoading: isLoadingTestimonials } = useTestimonials();
	const { isLoading: isLoadingPartners } = usePartners();
	const { isLoading: isLoadingStats } = useStats();
	const { data: siteSettings } = useSiteSettings();

	// Set up an additional delay to ensure all resources are loaded
	const [isFullyLoaded, setIsFullyLoaded] = useState(false);

	const isQueryLoading =
		isLoadingAbout ||
		isLoadingBlog ||
		isLoadingContact ||
		isLoadingPrivacy ||
		isLoadingTerms ||
		isLoadingWorkshops ||
		isLoadingServices ||
		isLoadingFaq ||
		isLoadingTestimonials ||
		isLoadingPartners ||
		isLoadingStats;

	const hasAllRequiredData =
		aboutData &&
		blogData &&
		contactData &&
		privacyData &&
		termsData &&
		workshopsData &&
		servicesData;

	useEffect(() => {
		// Check if all data has been fetched and set a slight delay for DOM rendering
		if (!isQueryLoading && hasAllRequiredData && !isFullyLoaded) {
			const timer = setTimeout(() => {
				setIsFullyLoaded(true);
			}, 300); // Add small delay to ensure DOM elements finish rendering

			return () => clearTimeout(timer);
		}
	}, [isQueryLoading, hasAllRequiredData, isFullyLoaded]);

	if (isStudio) {
		return (
			<Routes>
				<Route path='/studio/*' element={<SanityStudio />} />
			</Routes>
		);
	}

	return (
		<SiteSettingsProvider isBannerActive={!!siteSettings?.bookingNoticeActive}>
			{!isFullyLoaded && <Loader />}
			<ScrollToTop />
			<Header />
			<Routes>
				<Route path='/' element={<Index />} />
				<Route path='/services' element={<Services />} />
				<Route path='/services/:slug' element={<ServiceDetails />} />
				<Route path='/workshops/:id' element={<WorkshopDetailsPage />} />
				<Route path='/about' element={<About />} />
				<Route path='/blog' element={<Blog />} />
				<Route path='/blog/:id' element={<BlogPost />} />
				<Route path='/contact' element={<Contact />} />
				<Route path='/support' element={<Support />} />
				<Route path='/terms' element={<Terms />} />
				<Route path='/privacy' element={<Privacy />} />
				<Route path='/404' element={<NotFound />} />
				<Route path='*' element={<NotFound />} />
			</Routes>
		</SiteSettingsProvider>
	);
};

const App = () => {
	// Get the dehydrated state from the window object
	const dehydratedState: DehydratedState | undefined =
		typeof window !== 'undefined'
			? (window as Window & { __REACT_QUERY_STATE__?: DehydratedState })
					.__REACT_QUERY_STATE__
			: undefined;

	return (
		<QueryClientProvider client={queryClient}>
			<HydrationBoundary state={dehydratedState}>
				<BrowserRouter>
					<TooltipProvider>
						<Sonner />
						<AppContent />
					</TooltipProvider>
				</BrowserRouter>
			</HydrationBoundary>
		</QueryClientProvider>
	);
};

export default App;
